import type { RepeatSlot } from '@fieldops/shared'
import type { MouseEvent, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '../../lib/cn.js'
import { useJobHref } from '../../lib/job-overlay.js'
import { Icon } from '../Icon.js'
import { dayLabel, monthLabel } from './rule.js'

export type TrackRow = {
  id: string
  name: string
  sub: string
  slots: readonly RepeatSlot[]
  /** Right of the months: the next visit on the agreement page. */
  aside?: ReactNode
  /** Its visits still to come are drawn as paused rather than planned. */
  paused?: boolean
  /** The name opens the row's editor. Drawn only in the `dots` look. */
  onEdit?: () => void
  editLabel?: string
  /**
   * Drawn in each month instead of the row's slots: the agreement page's
   * Bookings row, under a site's services. Only in the `dots` look.
   */
  marks?: (month: string) => ReactNode
  /**
   * The agreement page's menu for one visit: a right-click on any of the
   * row's marks, or a left-click on one with no job, opens it at `at`.
   */
  onSlotMenu?: (slot: RepeatSlot, at: { x: number; y: number }) => void
}

/** Where a menu opened from a mark goes: at the pointer, or under the mark for a click from the keyboard. */
export function menuPoint(event: MouseEvent<HTMLElement>): { x: number; y: number } {
  // The context-menu key reports the pointer at 0, 0.
  const pointer = event.clientX !== 0 || event.clientY !== 0
  if (pointer && (event.type === 'contextmenu' || event.detail > 0)) return { x: event.clientX, y: event.clientY }
  const rect = event.currentTarget.getBoundingClientRect()
  return { x: rect.left, y: rect.bottom }
}

export type TrackGroup = { key: string; heading?: ReactNode; rows: readonly TrackRow[] }

/**
 * `track` joins a row's visits with a line, solid behind and dashed ahead —
 * the job card's Schedule tab. `dots` is the agreement page: loose marks, a
 * solid Charcoal for a job already on the schedule and a dashed edge for
 * planned.
 */
export type TrackLook = 'track' | 'dots'

export type Mark = 'done' | 'current' | 'booked' | 'planned' | 'overdue' | 'skipped' | 'paused'

export const MARK_LABEL: Record<Mark, string> = {
  done: 'Done',
  current: 'This visit',
  booked: 'On the schedule',
  planned: 'Planned',
  overdue: 'Overdue',
  skipped: 'Skipped',
  paused: 'Paused',
}

function markOf(slot: RepeatSlot, currentJobId: string | null, paused: boolean): Mark {
  if (currentJobId && slot.job?.id === currentJobId) return 'current'
  if (paused && !slot.job && (slot.state === 'NEXT' || slot.state === 'PLANNED' || slot.state === 'OVERDUE')) {
    return 'paused'
  }
  switch (slot.state) {
    case 'DONE':
      return 'done'
    case 'CREATED':
      return 'booked'
    case 'SKIPPED':
    case 'UNSUCCESSFUL':
      return 'skipped'
    case 'OVERDUE':
      return 'overdue'
    default:
      return 'planned'
  }
}

/** A visit that is behind the series: the solid, green part of its line. */
const settled = (mark: Mark) => mark === 'done' || mark === 'skipped'

export function SlotDot({ mark, small = false, look = 'track' }: { mark: Mark; small?: boolean; look?: TrackLook }) {
  const size = small ? 'size-2.5' : undefined
  // Square, like every mark that carries a tone (decision 110).
  const corner = small ? 'rounded-[2px]' : 'rounded-sm'
  if (look === 'dots') {
    const dot = cn(corner, size ?? 'size-3.5')
    switch (mark) {
      case 'done':
        return <span className={cn(dot, 'bg-brand-500 ring-2 ring-brand-100')} />
      case 'current':
      case 'booked':
        return <span className={cn(dot, 'bg-ink-900 ring-2 ring-ink-200')} />
      case 'overdue':
        return <span className={cn(dot, 'border-[1.5px] border-dashed border-red-600 bg-red-50')} />
      case 'skipped':
        return <span className={cn('rounded-[2px] bg-ink-200', size ?? 'size-2.5')} />
      case 'paused':
        return <span className={cn(dot, 'border-[1.5px] border-dotted border-ink-400 bg-white')} />
      default:
        return <span className={cn(dot, 'border-[1.5px] border-dashed border-ink-500 bg-white')} />
    }
  }
  switch (mark) {
    case 'done':
      return (
        <span className={cn('grid place-items-center bg-brand-500 text-ink-900', corner, size ?? 'size-5')}>
          {small ? null : <Icon name="check" className="size-3" strokeWidth={3} />}
        </span>
      )
    case 'current':
      return <span className={cn('bg-ink-900 ring-4 ring-ink-200', corner, size ?? 'size-5')} />
    case 'booked':
      return <span className={cn('border-2 border-ink-900 bg-white', corner, size ?? 'size-4')} />
    case 'overdue':
      return <span className={cn('border-2 border-red-600 bg-red-50', corner, size ?? 'size-4')} />
    case 'skipped':
      return <span className={cn('bg-ink-200', corner, size ?? 'size-3.5')} />
    default:
      return <span className={cn('border-2 border-dashed border-ink-500 bg-white', corner, size ?? 'size-3.5')} />
  }
}

/**
 * Services across the months of a term: one row each, a dot for every visit
 * in its month, joined by a line that is solid while it is behind and dashed
 * ahead. Today's month is a grey band down every row under a Charcoal
 * heading, like the board's now line. Decision 96.
 *
 * The slots are the server's `repeatSlots`, the same ones the job card's
 * timeline draws. A dot with a job opens it.
 */
export function ServiceTracks({
  months,
  today,
  groups,
  currentJobId = null,
  upcoming = null,
  label,
  showAside = false,
  asideLabel = 'Next',
  showKey = true,
  look = 'track',
  firstLabel = 'Service',
  footer = null,
}: {
  months: readonly string[]
  today: string
  groups: readonly TrackGroup[]
  /** The job the card is open on; its visit is "This visit". */
  currentJobId?: string | null
  /** The visit to act on next, ringed in green. */
  upcoming?: { rowId: string; sequence: number } | null
  label: string
  showAside?: boolean
  asideLabel?: string
  showKey?: boolean
  look?: TrackLook
  firstLabel?: string
  /** A last row across the whole grid, such as Add site. */
  footer?: ReactNode
}) {
  const jobHref = useJobHref()
  const dots = look === 'dots'
  const n = months.length
  const nowMonth = today.slice(0, 7)
  const years = new Set(months.map((month) => month.slice(0, 4)))
  const columns = dots
    ? `minmax(260px, 3.4fr) repeat(${n}, minmax(34px, 1fr))${showAside ? ' 80px' : ''}`
    : `minmax(170px, 1.7fr) repeat(${n}, minmax(34px, 1fr))${showAside ? ' 72px' : ''}`
  const anyPaused = groups.some((group) => group.rows.some((row) => row.paused))

  const band = (month: string) => month === nowMonth && 'bg-ink-100/80 shadow-[inset_1px_0_0_var(--color-ink-300),inset_-1px_0_0_var(--color-ink-300)]'

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-x-auto">
        <div role="table" aria-label={label} className="grid min-w-[640px]" style={{ gridTemplateColumns: columns }}>
          <div role="row" className="contents">
            <span
              role="columnheader"
              className={cn(
                'px-1 pb-2 text-[10px] font-bold tracking-[0.06em] text-ink-400 uppercase',
                dots && 'self-end border-b border-ink-100',
              )}
            >
              {firstLabel}
            </span>
            {months.map((month, index) => {
              const year =
                years.size > 1 && (index === 0 || month.endsWith('-01')) ? (
                  <span className={cn('text-[9.5px]', dots ? 'text-ink-300' : 'text-ink-400')}>{month.slice(0, 4)}</span>
                ) : null
              return (
                <span
                  key={month}
                  role="columnheader"
                  className={cn(
                    'flex flex-col items-center justify-end pb-2 text-[11px] leading-tight',
                    month === nowMonth ? 'font-bold text-ink-900' : 'text-ink-400',
                    dots && 'border-b border-ink-100',
                    band(month),
                  )}
                >
                  {dots ? null : year}
                  {monthLabel(month)}
                  {dots ? year : null}
                  {month === nowMonth ? <span className="text-[8.5px] tracking-wide uppercase">Today</span> : null}
                </span>
              )
            })}
            {showAside ? (
              <span
                role="columnheader"
                className={cn(
                  'pb-2 text-right text-[10px] font-bold tracking-[0.06em] text-ink-400 uppercase',
                  dots && 'self-end border-b border-ink-100',
                )}
              >
                {asideLabel}
              </span>
            ) : null}
          </div>

          {groups.map((group, groupIndex) => (
            <div key={group.key} role="rowgroup" className="contents">
              {group.heading ? (
                <div role="row" className="contents">
                  <div
                    role="rowheader"
                    className={cn(
                      'px-1',
                      dots ? cn('pt-4 pb-1.5', groupIndex > 0 && 'border-t border-ink-100') : 'border-t border-ink-100 pt-3 pb-1',
                    )}
                    style={{ gridColumn: '1 / -1' }}
                  >
                    {group.heading}
                  </div>
                </div>
              ) : null}
              {group.rows.map((row, rowIndex) => {
                const placed = row.slots
                  .map((slot) => ({
                    slot,
                    at: months.indexOf(slot.dueOn.slice(0, 7)),
                    mark: markOf(slot, currentJobId, row.paused ?? false),
                  }))
                  .filter((entry) => entry.at >= 0)
                const first = placed[0]?.at ?? -1
                const last = placed.at(-1)?.at ?? -1
                const lastSettled = [...placed].reverse().find((entry) => settled(entry.mark))?.at ?? first
                const x = (at: number) => `${((at + 0.5) / n) * 100}%`
                const rule = dots ? cn(rowIndex === group.rows.length - 1 && 'pb-2') : 'border-t border-ink-100'

                if (dots && row.marks) {
                  const marks = row.marks
                  const sum = cn(rule, 'border-t border-dashed border-ink-200')
                  return (
                    <div key={row.id} role="row" aria-label={row.name} className="contents">
                      <div role="rowheader" className={cn('flex min-w-0 items-baseline gap-2 py-1.5 pr-1 pl-4', sum)}>
                        <span className="max-w-full shrink-0 truncate text-[13px] font-semibold text-ink-900">{row.name}</span>
                        <span className="min-w-0 truncate text-[11.5px] text-ink-400">{row.sub}</span>
                      </div>
                      <div className={cn('grid', sum)} style={{ gridColumn: `2 / span ${n}`, gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>
                        {months.map((month) => (
                          <div key={month} role="cell" className={cn('flex items-center justify-center gap-0.5 py-1.5', band(month))}>
                            {marks(month)}
                          </div>
                        ))}
                      </div>
                      {showAside ? (
                        <div role="cell" className={cn('flex items-center justify-end text-[12.5px] font-semibold whitespace-nowrap text-ink-900 tabular-nums', sum)}>
                          {row.aside}
                        </div>
                      ) : null}
                    </div>
                  )
                }

                return (
                  <div key={row.id} role="row" aria-label={row.name} className="contents">
                    {dots && row.onEdit ? (
                      <div role="rowheader" className={cn('flex min-w-0 py-1 pr-1 pl-2.5', rule)}>
                        <button
                          type="button"
                          onClick={row.onEdit}
                          aria-label={row.editLabel ?? `Edit ${row.name}`}
                          className="group/edit flex min-w-0 flex-1 items-baseline gap-2 rounded-md px-1.5 py-0.5 text-left hover:bg-ink-50"
                        >
                          <span className="max-w-full shrink-0 truncate text-[13px] font-medium text-ink-900 group-hover/edit:text-brand-700">
                            {row.name}
                          </span>
                          <span className="min-w-0 truncate text-[11.5px] text-ink-400">{row.sub}</span>
                          <Icon
                            name="pencil"
                            className="ml-auto size-3.5 shrink-0 self-center text-ink-400 opacity-0 group-hover/edit:opacity-100 group-focus-visible/edit:opacity-100"
                          />
                        </button>
                      </div>
                    ) : dots ? (
                      <div role="rowheader" className={cn('flex min-w-0 items-baseline gap-2 py-1.5 pr-1 pl-4', rule)}>
                        <span className="max-w-full shrink-0 truncate text-[13px] font-medium text-ink-900">{row.name}</span>
                        <span className="min-w-0 truncate text-[11.5px] text-ink-400">{row.sub}</span>
                      </div>
                    ) : (
                      <div role="rowheader" className="min-w-0 border-t border-ink-100 px-1 py-2.5">
                        <span className="block truncate text-[13px] font-semibold text-ink-900">{row.name}</span>
                        <span className="block text-[11.5px] leading-snug text-ink-500">{row.sub}</span>
                      </div>
                    )}
                    <div className={cn('relative grid', rule)} style={{ gridColumn: `2 / span ${n}`, gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>
                      {!dots && first >= 0 && last > first ? (
                        <>
                          {lastSettled > first ? (
                            <span
                              aria-hidden="true"
                              className="absolute top-1/2 h-0.5 -translate-y-1/2 bg-brand-500"
                              style={{ left: x(first), width: `${((lastSettled - first) / n) * 100}%` }}
                            />
                          ) : null}
                          {last > lastSettled ? (
                            <span
                              aria-hidden="true"
                              className="absolute top-1/2 -translate-y-1/2 border-t-2 border-dashed border-ink-300"
                              style={{ left: x(lastSettled), width: `${((last - lastSettled) / n) * 100}%` }}
                            />
                          ) : null}
                        </>
                      ) : null}
                      {months.map((month, index) => {
                        const here = placed.filter((entry) => entry.at === index)
                        return (
                          <div key={month} role="cell" className={cn('relative flex items-center justify-center gap-0.5', dots ? 'py-1.5' : 'py-2', band(month))}>
                            {here.map(({ slot, mark }) => {
                              const title = [
                                MARK_LABEL[mark],
                                dayLabel(slot.dueOn),
                                !slot.job && slot.moved ? 'moved' : null,
                                !slot.job && slot.note ? 'has a note' : null,
                              ]
                                .filter(Boolean)
                                .join(' · ')
                              const ringed = upcoming?.rowId === row.id && upcoming.sequence === slot.sequence
                              const dot = (
                                <span
                                  className={cn(
                                    'grid place-items-center',
                                    ringed ? 'rounded-md bg-white p-1 ring-2 ring-brand-500' : 'bg-white/0',
                                  )}
                                >
                                  <SlotDot mark={mark} small={here.length > 2} look={look} />
                                </span>
                              )
                              const onSlotMenu = row.onSlotMenu
                              const onContextMenu = onSlotMenu
                                ? (event: MouseEvent<HTMLElement>) => {
                                    event.preventDefault()
                                    onSlotMenu(slot, menuPoint(event))
                                  }
                                : undefined
                              return slot.job && mark !== 'current' ? (
                                <Link
                                  key={slot.sequence}
                                  to={jobHref(slot.job.id)}
                                  title={title}
                                  aria-label={title}
                                  onContextMenu={onContextMenu}
                                  className="relative rounded-sm hover:ring-2 hover:ring-ink-200"
                                >
                                  {dot}
                                </Link>
                              ) : onSlotMenu ? (
                                <button
                                  key={slot.sequence}
                                  type="button"
                                  title={title}
                                  aria-label={title}
                                  aria-haspopup="menu"
                                  onClick={(event) => onSlotMenu(slot, menuPoint(event))}
                                  onContextMenu={onContextMenu}
                                  className="relative grid place-items-center rounded-sm hover:ring-2 hover:ring-ink-200"
                                >
                                  {dot}
                                </button>
                              ) : (
                                <span key={slot.sequence} role="img" title={title} aria-label={title} className="relative">
                                  {dot}
                                </span>
                              )
                            })}
                          </div>
                        )
                      })}
                    </div>
                    {showAside ? (
                      <div
                        role="cell"
                        className={cn(
                          'flex items-center justify-end text-[12.5px] whitespace-nowrap tabular-nums',
                          dots ? cn('font-semibold text-ink-900', rule) : 'border-t border-ink-100 text-ink-700',
                        )}
                      >
                        {row.aside}
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
          ))}

          {footer ? (
            <div role="row" className="contents">
              <div role="cell" className="border-t border-ink-100 pt-2" style={{ gridColumn: '1 / -1' }}>
                {footer}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {showKey ? (
        <SlotKey
          look={look}
          marks={['done', ...(currentJobId ? ['current' as const] : []), 'booked', 'planned', anyPaused ? 'paused' : 'skipped']}
        />
      ) : null}
    </div>
  )
}

/** What each mark means, in the look it is drawn in. */
export function SlotKey({ marks, look = 'track', className }: { marks: readonly Mark[]; look?: TrackLook; className?: string }) {
  return (
    <ul aria-label="Key" className={cn('flex flex-wrap items-center gap-4 text-[12px] text-ink-600', className)}>
      {marks.map((mark) => (
        <li key={mark} className="flex items-center gap-1.5">
          <span aria-hidden="true" className={cn('grid place-items-center', look === 'dots' ? 'w-4' : 'w-5')}>
            <SlotDot mark={mark} small={false} look={look} />
          </span>
          {MARK_LABEL[mark]}
        </li>
      ))}
    </ul>
  )
}
